import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  MenuItem,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  EventAvailable as EventAvailableIcon,
  CalendarMonth as CalendarMonthIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HowToReg as HowToRegIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { appointmentAPI, patientAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import StatCard from '../components/StatCard';
import PageHeader from '../components/PageHeader';

const statusColors = {
  booked: 'info',
  confirmed: 'primary',
  arrived: 'success',
  completed: 'success',
  cancelled: 'error',
  'no-show': 'warning',
};

const emptyForm = {
  patient: null,
  appointmentDate: new Date().toISOString().split('T')[0],
  appointmentTime: '',
  reason: '',
  notes: '',
  source: 'phone',
};

const Appointments = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming' | 'date' | 'all'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [openBookDialog, setOpenBookDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openCheckInDialog, setOpenCheckInDialog] = useState(false);
  const [checkInTarget, setCheckInTarget] = useState(null);
  const [referTo, setReferTo] = useState('optometrist');

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchAppointments = useCallback(async () => {
    try {
      const params = {};
      if (viewMode === 'date') {
        params.date = selectedDate;
      } else if (viewMode === 'upcoming') {
        params.upcoming = 'true';
        params.status = 'booked';
        params.limit = 500;
      } else {
        params.limit = 500;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
      }
      if (statusFilter) params.status = statusFilter;
      const res = await appointmentAPI.getAppointments(params);
      setAppointments(res.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch appointments', 'error');
    }
  }, [selectedDate, viewMode, dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    fetchAppointments();
    patientAPI.getPatients({ limit: 200 }).then(res => setPatients(res.data.data)).catch(() => {});
  }, [fetchAppointments]);

  const stats = {
    total: appointments.length,
    booked: appointments.filter(a => a.status === 'booked' || a.status === 'confirmed').length,
    arrived: appointments.filter(a => a.status === 'arrived').length,
    completed: appointments.filter(a => a.status === 'completed').length,
  };

  const handleBook = async () => {
    if (!form.patient || !form.appointmentDate || !form.appointmentTime || !form.reason) {
      showSnackbar('Please fill all required fields', 'error');
      return;
    }
    try {
      await appointmentAPI.createAppointment({
        patientId: form.patient._id,
        appointmentDate: form.appointmentDate,
        appointmentTime: form.appointmentTime,
        reason: form.reason,
        notes: form.notes,
        source: form.source,
      });
      showSnackbar('Appointment booked successfully!');
      setOpenBookDialog(false);
      setForm(emptyForm);
      fetchAppointments();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to book appointment', 'error');
    }
  };

  const handleCancel = async (id) => {
    try {
      await appointmentAPI.cancelAppointment(id);
      showSnackbar('Appointment cancelled');
      fetchAppointments();
    } catch (error) {
      showSnackbar('Failed to cancel appointment', 'error');
    }
  };

  const openCheckIn = (appt) => {
    setCheckInTarget(appt);
    setReferTo('optometrist');
    setOpenCheckInDialog(true);
  };

  const handleCheckIn = async () => {
    try {
      await appointmentAPI.checkInAppointment(checkInTarget._id, { referTo });
      showSnackbar(`Checked in! Token issued for ${checkInTarget.patient?.name}`);
      setOpenCheckInDialog(false);
      setCheckInTarget(null);
      fetchAppointments();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to check in', 'error');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight="800">Appointments &amp; Queue</Typography>
            <Typography variant="body2" color="text.secondary">Book, track and check in patient appointments</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(e, v) => v && setViewMode(v)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 } }}
            >
              <ToggleButton value="upcoming">Upcoming</ToggleButton>
              <ToggleButton value="date">By Date</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
            {viewMode === 'date' && (
              <TextField
                type="date"
                size="small"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            )}
            {viewMode === 'all' && (
              <>
                <TextField
                  type="date"
                  size="small"
                  label="From"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="To"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
            <TextField
              select
              size="small"
              label="Status"
              value={viewMode === 'upcoming' ? 'booked' : statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              disabled={viewMode === 'upcoming'}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="booked">Booked</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="arrived">Arrived</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="no-show">No-show</MenuItem>
            </TextField>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenBookDialog(true)}>
              Book Appointment
            </Button>
            <IconButton onClick={fetchAppointments} sx={{ border: '1px solid #E9E9F2', color: 'text.secondary', '&:hover': { bgcolor: '#F4F4F9' } }}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatCard
            label={viewMode === 'all' ? 'Total Appointments' : viewMode === 'upcoming' ? 'Upcoming' : 'Total Today'}
            value={stats.total}
            icon={<CalendarMonthIcon fontSize="small" />}
            color="primary"
          />
          <StatCard label="Booked" value={stats.booked} icon={<EventAvailableIcon fontSize="small" />} color="info" />
          <StatCard label="Arrived" value={stats.arrived} icon={<HowToRegIcon fontSize="small" />} color="success" />
          <StatCard label="Completed" value={stats.completed} icon={<CheckCircleIcon fontSize="small" />} color="secondary" />
        </Box>
      </Paper>

      {/* Appointment List */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {viewMode !== 'date' && <TableCell>Date</TableCell>}
                <TableCell>Time</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={viewMode !== 'date' ? 8 : 7} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      {viewMode === 'date' ? 'No appointments for this date' : viewMode === 'upcoming' ? 'No upcoming appointments' : 'No appointments found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                appointments.map((appt) => (
                  <TableRow key={appt._id} hover>
                    {viewMode !== 'date' && (
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {appt.appointmentDate
                            ? new Date(appt.appointmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{appt.appointmentTime}</Typography>
                      <Typography variant="caption" color="text.secondary">{appt.appointmentId}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 34, height: 34, bgcolor: 'secondary.light' }}>
                          {appt.patient?.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{appt.patient?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{appt.patient?.patientId}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2">{appt.patient?.contact?.phone}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="body2">{appt.reason}</Typography></TableCell>
                    <TableCell><Chip size="small" label={appt.source} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={appt.status.replace('-', ' ')}
                        color={statusColors[appt.status] || 'default'}
                        sx={{ textTransform: 'capitalize', fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {(appt.status === 'booked' || appt.status === 'confirmed') && (
                        <>
                          <Tooltip title="Check-in &amp; issue token">
                            <Button size="small" variant="contained" color="success" startIcon={<HowToRegIcon />} onClick={() => openCheckIn(appt)} sx={{ mr: 1 }}>
                              Check-in
                            </Button>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton size="small" color="error" onClick={() => handleCancel(appt._id)}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {appt.status === 'arrived' && (
                        <Chip size="small" label={`Token issued`} color="success" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Book Appointment Dialog */}
      <Dialog open={openBookDialog} onClose={() => setOpenBookDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Book New Appointment
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={patients}
                getOptionLabel={(o) => `${o.name} (${o.patientId}) - ${o.contact?.phone || ''}`}
                value={form.patient}
                onChange={(e, v) => setForm({ ...form, patient: v })}
                renderInput={(params) => <TextField {...params} label="Select Patient *" placeholder="Search by name, ID or phone" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Date *"
                value={form.appointmentDate}
                onChange={(e) => setForm({ ...form, appointmentDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="time"
                label="Time *"
                value={form.appointmentTime}
                onChange={(e) => setForm({ ...form, appointmentTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Source"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
              >
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="walk-in">Walk-in</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="referral">Referral</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Visit *"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g., Eye checkup, Follow-up, Cataract consultation"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBookDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBook}>Book Appointment</Button>
        </DialogActions>
      </Dialog>

      {/* Check-in Dialog */}
      <Dialog open={openCheckInDialog} onClose={() => setOpenCheckInDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Check-in Patient</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Checking in <strong>{checkInTarget?.patient?.name}</strong> will issue a token number and create a visit.
          </Typography>
          <TextField
            fullWidth
            select
            label="Refer To *"
            value={referTo}
            onChange={(e) => setReferTo(e.target.value)}
          >
            <MenuItem value="optometrist">Optometrist</MenuItem>
            <MenuItem value="doctor">Doctor</MenuItem>
            <MenuItem value="opd">OPD (Billing/Dispensing)</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCheckInDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleCheckIn}>Confirm Check-in</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Appointments;
